import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { toast, Slide } from 'react-toastify';
import {
  schoolRegistrationConfirmationSchema,
  type SchoolRegistrationConfirmationFormData,
  type SchoolRegistrationConfirmationFormInput,
} from '../validation/confirmation.schemas';
import { schoolRequestsApi } from '../api/school-requests.api';
import { useAuthProviderV2, useRoutesProvider } from '@/providers/provider-hooks';
import { resolvePostAuthTarget } from '@/domains/auth/utils/resolve-post-auth-target';
import { pasiInscriereActive } from '@/domains/auth/utils/pasi-inscriere';
import type { UserInfo } from '@/providers/auth-provider.types';
import { updateFailureToast, updateSuccessToast } from '@/toast';
import { getErrorMessage } from '@/core/api/response-handler';
import { applyServerFieldErrors } from '@/shared/utils';
import { trackSchoolConfirmationSubmitted } from '@/telemetry/posthog-events';

/**
 * Profesorul care revine: „Confirm participarea" (cu regulamentul bifat) sau „Nu particip".
 *
 * Dupa raspuns se ia un token NOU (claim-urile de stare se recalculeaza in backend: cererea
 * noua e APPROVED, deci needsSchoolRegistrationConfirmation devine false) si se navigheaza
 * prin resolvePostAuthTarget cu pathname=null — aceeasi cale ca login-ul. Fara setTimeout,
 * fara navigate('/') (care duce la pagina de login, vezi outstanding-issues 2.14).
 */
export function useSchoolConfirmationWithForm() {
  const navigate = useNavigate();
  const { handleTokenRefresh } = useAuthProviderV2();
  const { PATHS, PAGES, ROLES, getUserHomePage } = useRoutesProvider();

  const form = useForm<SchoolRegistrationConfirmationFormInput, unknown, SchoolRegistrationConfirmationFormData>({
    resolver: zodResolver(schoolRegistrationConfirmationSchema),
    defaultValues: { amCititSiSuntDeAcord: false },
  });

  const navigateAfterRefresh = async () => {
    const token = await handleTokenRefresh(true);
    if (!token) {
      navigate(getUserHomePage(), { replace: true });
      return;
    }
    const decoded = jwtDecode<UserInfo>(token);
    const target =
      resolvePostAuthTarget(decoded, null, {
        PATHS,
        PAGES,
        ROLES,
        getUserHomePage,
        pasiInscriere: pasiInscriereActive(),
      }) ??
      getUserHomePage(decoded.role);
    navigate(target, { replace: true });
  };

  const mutation = useMutation({
    mutationFn: (payload: { wantsToRegisterSchool: boolean; amCititSiSuntDeAcord: boolean }) =>
      schoolRequestsApi.confirmUpdate(payload),
    onError: (error) => applyServerFieldErrors(error, form.setError),
  });

  const run = async (payload: { wantsToRegisterSchool: boolean; amCititSiSuntDeAcord: boolean }, successMessage: string) => {
    const id = toast.loading('Se procesează...', { transition: Slide });
    try {
      await mutation.mutateAsync(payload);
      trackSchoolConfirmationSubmitted(payload.wantsToRegisterSchool);
      updateSuccessToast(successMessage, id);
      await navigateAfterRefresh();
    } catch (error: unknown) {
      updateFailureToast(
        getErrorMessage(error, '') || 'A apărut o problemă la procesarea confirmării. Vă rugăm să încercați din nou.',
        id
      );
    }
  };

  /** Butonul primar: cere regulamentul bifat (validat de zod), apoi confirma. */
  const confirm = form.handleSubmit((data) =>
    run({ wantsToRegisterSchool: true, amCititSiSuntDeAcord: data.amCititSiSuntDeAcord },
        'Participarea a fost confirmată. Cererea așteaptă aprobarea echipei ECOTIC.')
  );

  /** Butonul secundar: nu cere regulamentul. Backend-ul dezleaga scoala de profesor. */
  const decline = () =>
    run({ wantsToRegisterSchool: false, amCititSiSuntDeAcord: false },
        'Am înregistrat că nu participați în această ediție.');

  // `mutation.isPending` se aprinde abia dupa ce rezolverul zod (asincron) a trecut si React a
  // comis re-randarea; pana atunci CTA-ul ramane activ si o a doua apasare porneste un al doilea
  // `run()`, cu propriul toast de succes. `formState.isSubmitting` se aprinde din prima instructiune
  // a lui handleSubmit, deci inchide fereastra.
  return {
    form,
    confirm,
    decline,
    isSubmitting: form.formState.isSubmitting || mutation.isPending,
  };
}
